/**
 * The order's Kits step panel (V1 completion R4; the agreed mock-up's Kits tab, drawn as a step of the rail because
 * the order page has no tabs, Ben, 3 Oct): the kit lines of an order a page at a time, each a card holding what one
 * kit contains, how its drops are delivered and where they have got to; a card opens its drops (a page of 50, searched
 * on the server); the kit manifest downloads as a CSV.
 *
 * A kit line is a number of identical kits, so the panel never holds every drop: it reads the kit lines a page at a
 * time from the Kits step's URL and a line's drops from the line's own. Cost and margin are staff's; every word is
 * the view's.
 */
import { useEffect, useRef, useState } from "react";
import { Download, Search } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  KitContent,
  KitDrop,
  KitDrops,
  KitLine,
  OrderKits,
  OrderKitsSummary,
} from "@shared-core/data/order-to-invoice-types";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import { Button } from "../../primitives/button";
import { Checkbox } from "../../primitives/checkbox";
import { Input } from "../../primitives/input";
import { cn } from "../../primitives/utils";
import { ErrorPanel } from "../../patterns/ErrorPanel";
import { ListPagination } from "../../patterns/ListPagination";
import { StatusPill } from "./OrderPanels";
import { PanelHeading, useFocusRef } from "./StepPanelParts";

/** Kit lines a page; a kit line's drops a page. */
export const KIT_LINES_PAGE_SIZE = 10;
export const KIT_DROPS_PAGE_SIZE = 50;

type Read<T> =
  | { kind: "loading" }
  | { kind: "failed"; message: string }
  | { kind: "loaded"; value: T };

/** One read from the adapter, again when `key` changes or `retry` is asked for; an older answer never lands late. */
function useRead<T>(read: () => Promise<T>, key: string) {
  const [state, setState] = useState<Read<T>>({ kind: "loading" });
  const [attempt, setAttempt] = useState(0);
  const latest = useRef(read);
  latest.current = read;
  useEffect(() => {
    let isCurrent = true;
    setState((s) => (s.kind === "loaded" ? s : { kind: "loading" }));
    latest
      .current()
      .then((value) => {
        if (isCurrent) setState({ kind: "loaded", value });
      })
      .catch((err: unknown) => {
        if (!isCurrent) return;
        setState({
          kind: "failed",
          message:
            err instanceof Error ? err.message : "Could not load the kits.",
        });
      });
    return () => {
      isCurrent = false;
    };
  }, [key, attempt]);
  return { state, retry: () => setAttempt((n) => n + 1) };
}

export interface KitsStepHandlers {
  busy: boolean;
  /** The kit manifest (drops × contents) as a CSV. */
  onDownloadManifest: (manifestUrl: string) => void;
  /**
   * *Record despatched…* for one drop of an in-house kit (orders' command on the kit line, a made line's despatch):
   * offered on the drops of a kit line only where the line carries it enabled; undefined offers it nowhere.
   */
  recordDespatched?: {
    canRecord: (lineRef: string) => boolean;
    open: (lineRef: string, deliveryLineId: string) => void;
  };
  /**
   * *Despatch together…* for the drops of an in-house kit (orders' command on the kit line): `dropNumbers` are the
   * drops ticked on the Kits tab, or none to start from every drop still waiting. Undefined offers it nowhere.
   */
  despatchTogether?: {
    canDespatch: (lineRef: string) => boolean;
    open: (lineRef: string, dropNumbers: number[]) => void;
  };
}

export function KitsStepPanel({
  kits,
  focusLineNo,
  handlers,
}: {
  kits: OrderKitsSummary;
  focusLineNo: string | null;
  handlers: KitsStepHandlers;
}) {
  const adapter = useDataAdapter();
  const [page, setPage] = useState(1);
  // The step's own words change as drops go (its status lines), so the page reads again after a despatch.
  const fresh = `${kits.summary}|${kits.status_lines.join("|")}`;
  const { state, retry } = useRead(
    () =>
      adapter.getOrderKits(kits.kits_url, {
        page,
        pageSize: KIT_LINES_PAGE_SIZE,
      }),
    `${kits.kits_url}|${page}|${fresh}`,
  );
  return (
    <div className="space-y-3" data-section-id="order.kits">
      <PanelHeading
        title="Kits"
        info="A kit line is a number of identical kits. Each kit holds the line's contents: the order's lines, made or bought and sent to where the kits are packed, and stock picked there. Its drops are the places the kits go, a number of kits to each. Kits whose contents differ are separate lines."
        infoLabel="About kits"
        summary={kits.summary}
        action={
          <Button
            size="sm"
            variant="outline"
            disabled={handlers.busy}
            onClick={() => handlers.onDownloadManifest(kits.manifest_url)}
          >
            <Download aria-hidden className="size-4" />
            Kit manifest (CSV)
          </Button>
        }
      />
      {state.kind === "loading" && (
        <p
          role="status"
          className="rounded-lg border border-border p-6 text-center text-sm text-muted-foreground"
        >
          Loading the kits…
        </p>
      )}
      {state.kind === "failed" && (
        <ErrorPanel message={state.message} onRetry={retry} />
      )}
      {state.kind === "loaded" && (
        <KitLines
          kits={state.value}
          fresh={fresh}
          focusLineNo={focusLineNo}
          handlers={handlers}
          onPage={setPage}
        />
      )}
    </div>
  );
}

function KitLines({
  kits,
  fresh,
  focusLineNo,
  handlers,
  onPage,
}: {
  kits: OrderKits;
  /** Changes when the order's drops have moved, so an open list of drops reads again. */
  fresh: string;
  focusLineNo: string | null;
  handlers: KitsStepHandlers;
  onPage: (page: number) => void;
}) {
  if (kits.lines.length === 0)
    return (
      <p className="rounded-lg border border-border p-6 text-center text-sm text-muted-foreground">
        This order has no kit lines.
      </p>
    );
  return (
    <>
      <ul className="space-y-3" aria-label="Kit lines">
        {kits.lines.map((line) => (
          <li key={line.line_ref}>
            <KitLineCard
              line={line}
              fresh={fresh}
              isFocused={line.line_no === focusLineNo}
              handlers={handlers}
            />
          </li>
        ))}
      </ul>
      <ListPagination
        page={kits.page}
        totalPages={kits.total_pages}
        onPageChange={onPage}
      />
    </>
  );
}

/** One kit line: its status, where it is kitted and what it earns, then what one kit holds and where the drops stand. */
export function KitLineCard({
  line,
  fresh = "",
  isFocused = false,
  handlers,
}: {
  line: KitLine;
  fresh?: string;
  isFocused?: boolean;
  handlers: KitsStepHandlers;
}) {
  const focusRef = useFocusRef();
  const [showingDrops, setShowingDrops] = useState(false);
  return (
    <section
      ref={isFocused ? focusRef : undefined}
      aria-label={`${line.line_no} ${line.heading}`}
      className={cn(
        "rounded-lg border border-border bg-card p-4",
        isFocused && "border-l-4 border-l-primary",
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-base font-semibold">
          <span className="tabular-nums">{line.line_no}</span> {line.heading}
          {showingDrops && (
            <span className="font-normal text-muted-foreground"> · drops</span>
          )}
        </h4>
        <StatusPill tone={line.status.tone}>{line.status.label}</StatusPill>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {kitLineSummary(line)}
      </p>
      {showingDrops ? (
        <KitDropsTable
          line={line}
          fresh={fresh}
          handlers={handlers}
          onBack={() => setShowingDrops(false)}
        />
      ) : (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <KitContents contents={line.contents} />
          <KitDropsSummary
            line={line}
            handlers={handlers}
            onShowDrops={() => setShowingDrops(true)}
          />
        </div>
      )}
    </section>
  );
}

/** "Kitted at Group 4 Solutions · 250 kits · £3,000.00 (£12.00 a kit) · cost £950.00 + picks · margin 68%". */
function kitLineSummary(line: KitLine): string {
  return [
    `Kitted at ${line.kitted_at}`,
    `${line.kits.toLocaleString("en-GB")} ${line.kits === 1 ? "kit" : "kits"}`,
    line.price_pence !== null
      ? `${formatQuotedPrice(line.price_pence)}${
          line.price_per_kit_pence !== null
            ? ` (${formatQuotedPrice(line.price_per_kit_pence)} a kit)`
            : ""
        }`
      : null,
    line.cost_label,
    line.margin_pct !== null ? `margin ${line.margin_pct}%` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

function KitContents({ contents }: { contents: KitContent[] }) {
  return (
    <div className="rounded-md border border-dashed border-border bg-muted/30 p-3">
      <h5 className="mb-1 text-sm font-semibold">Contents (one kit)</h5>
      {contents.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing yet.</p>
      ) : (
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-sm">
          {contents.map((content, i) => (
            <KitContentRow key={`${content.label}-${i}`} content={content} />
          ))}
        </dl>
      )}
    </div>
  );
}

function KitContentRow({ content }: { content: KitContent }) {
  return (
    <>
      <dt className="text-right tabular-nums text-muted-foreground">
        {content.per_kit.toLocaleString("en-GB")}
      </dt>
      <dd>
        {content.label}
        {content.version_label && (
          <span className="text-muted-foreground">
            {" "}
            · {content.version_label}
          </span>
        )}{" "}
        <span className="text-xs text-muted-foreground">
          {content.status_label}
        </span>
      </dd>
    </>
  );
}

/** Whether the kit line's drops can be despatched together here: in-house, offered the command, some still waiting. */
function canDespatchTogether(
  line: KitLine,
  handlers: KitsStepHandlers,
): boolean {
  const together = handlers.despatchTogether;
  return (
    together !== undefined &&
    !line.is_bought &&
    line.drops.despatched < line.drops.drops &&
    together.canDespatch(line.line_ref)
  );
}

function KitDropsSummary({
  line,
  handlers,
  onShowDrops,
}: {
  line: KitLine;
  handlers: KitsStepHandlers;
  onShowDrops: () => void;
}) {
  const together = canDespatchTogether(line, handlers)
    ? handlers.despatchTogether
    : undefined;
  return (
    <div className="rounded-md border border-dashed border-border bg-muted/30 p-3">
      <h5 className="mb-1 text-sm font-semibold">Drops</h5>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-sm">
        <dt className="text-muted-foreground">Drops</dt>
        <dd>
          {line.drops.drops.toLocaleString("en-GB")}{" "}
          {line.drops.drops === 1 ? "drop" : "drops"} ·{" "}
          {line.kits.toLocaleString("en-GB")} {line.kits === 1 ? "kit" : "kits"}
        </dd>
        <dt className="text-muted-foreground">Delivery</dt>
        <dd>
          {line.delivery.line_no && (
            <span className="tabular-nums">{line.delivery.line_no} </span>
          )}
          {line.delivery.label}
          {line.delivery.detail && (
            <span className="block text-xs text-muted-foreground">
              {line.delivery.detail}
            </span>
          )}
        </dd>
        {line.drops.label && (
          <>
            <dt className="text-muted-foreground">Status</dt>
            <dd>{line.drops.label}</dd>
          </>
        )}
      </dl>
      {line.drops.drops > 0 && (
        <div className="flex flex-wrap items-center gap-3 pt-2">
          <Button
            type="button"
            variant="link"
            className="h-auto px-0"
            onClick={onShowDrops}
          >
            Show drops
          </Button>
          {together && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={handlers.busy}
              aria-label={`Despatch together: ${line.line_no} ${line.heading}`}
              onClick={() => together.open(line.line_ref, [])}
            >
              Despatch together…
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// ── A kit line's drops ────────────────────────────────────────────────────

const SEARCH_DEBOUNCE_MS = 300;

/** A kit line's drops, 50 at a time, searched by place or postcode on the server. */
export function KitDropsTable({
  line,
  fresh = "",
  handlers,
  onBack,
}: {
  line: KitLine;
  fresh?: string;
  handlers: KitsStepHandlers;
  onBack: () => void;
}) {
  const adapter = useDataAdapter();
  const [typed, setTyped] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(typed.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [typed]);
  const { state, retry } = useRead(
    () =>
      adapter.getKitDrops(line.drops_url, {
        q: q || undefined,
        page,
        pageSize: KIT_DROPS_PAGE_SIZE,
      }),
    `${line.drops_url}|${q}|${page}|${fresh}`,
  );
  const record = handlers.recordDespatched;
  const recordable =
    record && !line.is_bought && record.canRecord(line.line_ref)
      ? record.open
      : undefined;
  const together = canDespatchTogether(line, handlers)
    ? handlers.despatchTogether
    : undefined;
  // The drops ticked, by their number on the line, kept across the pages of the table until the order moves.
  const [ticked, setTicked] = useState<ReadonlySet<number>>(new Set());
  useEffect(() => setTicked(new Set()), [fresh]);
  const toggle = (number: number, isTicked: boolean) =>
    setTicked((current) => {
      const next = new Set(current);
      if (isTicked) next.add(number);
      else next.delete(number);
      return next;
    });
  return (
    <div className="mt-3 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button
          type="button"
          variant="link"
          className="h-auto px-0"
          onClick={onBack}
        >
          Back to {line.heading}
        </Button>
        <div className="relative w-full max-w-xs">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground"
          />
          <Input
            type="search"
            className="pl-8"
            placeholder="Search drops"
            aria-label={`Search the drops of ${line.line_no}`}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
          />
        </div>
      </div>
      {state.kind === "loading" && (
        <p role="status" className="text-sm text-muted-foreground">
          Loading the drops…
        </p>
      )}
      {state.kind === "failed" && (
        <ErrorPanel message={state.message} onRetry={retry} />
      )}
      {together && (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={handlers.busy}
            onClick={() =>
              together.open(
                line.line_ref,
                [...ticked].sort((a, b) => a - b),
              )
            }
          >
            Despatch together…
            {ticked.size > 0 &&
              ` (${ticked.size.toLocaleString("en-GB")} ticked)`}
          </Button>
          <span className="text-xs text-muted-foreground">
            {ticked.size > 0
              ? "Records the drops ticked."
              : "Tick drops to record, or start from every drop still waiting."}
          </span>
        </div>
      )}
      {state.kind === "loaded" && (
        <DropRows
          drops={state.value}
          isSearching={q !== ""}
          onRecordDespatched={
            recordable
              ? (deliveryLineId) => recordable(line.line_ref, deliveryLineId)
              : undefined
          }
          ticks={together ? { ticked, toggle } : undefined}
          busy={handlers.busy}
          onPage={setPage}
        />
      )}
    </div>
  );
}

function DropRows({
  drops,
  isSearching,
  onRecordDespatched,
  ticks,
  busy,
  onPage,
}: {
  drops: KitDrops;
  isSearching: boolean;
  onRecordDespatched?: (deliveryLineId: string) => void;
  /** A tick for each drop still waiting, by its number on the line: the drops to despatch together. */
  ticks?: {
    ticked: ReadonlySet<number>;
    toggle: (number: number, isTicked: boolean) => void;
  };
  busy: boolean;
  onPage: (page: number) => void;
}) {
  if (drops.items.length === 0)
    return (
      <p className="py-4 text-center text-sm text-muted-foreground">
        {isSearching
          ? "No drop matches that search."
          : "This kit line has no drops."}
      </p>
    );
  const first = (drops.page - 1) * drops.page_size + 1;
  const last = first + drops.items.length - 1;
  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              {ticks && (
                <th scope="col" className="w-8 py-1.5 pr-2 font-normal">
                  <span className="sr-only">Tick to despatch</span>
                </th>
              )}
              <th scope="col" className="py-1.5 pr-3 font-normal">
                Drop
              </th>
              <th scope="col" className="py-1.5 pr-3 font-normal">
                Postcode
              </th>
              <th scope="col" className="py-1.5 pr-3 text-right font-normal">
                Kits
              </th>
              <th scope="col" className="py-1.5 pr-3 font-normal">
                Status
              </th>
              <th scope="col" className="py-1.5 font-normal">
                Carrier
              </th>
              {onRecordDespatched && (
                <th scope="col" className="py-1.5">
                  <span className="sr-only">Record</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {drops.items.map((drop) => (
              <DropRow
                key={drop.id}
                drop={drop}
                busy={busy}
                onRecordDespatched={onRecordDespatched}
                ticks={ticks}
              />
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground" role="status">
        {isSearching ? "Matches" : "Rows"} {first.toLocaleString("en-GB")}–
        {last.toLocaleString("en-GB")} of {drops.total.toLocaleString("en-GB")}
      </p>
      <ListPagination
        page={drops.page}
        totalPages={drops.total_pages}
        onPageChange={onPage}
      />
    </>
  );
}

function DropRow({
  drop,
  busy,
  onRecordDespatched,
  ticks,
}: {
  drop: KitDrop;
  busy: boolean;
  onRecordDespatched?: (deliveryLineId: string) => void;
  ticks?: {
    ticked: ReadonlySet<number>;
    toggle: (number: number, isTicked: boolean) => void;
  };
}) {
  const carrier = [drop.carrier, drop.tracking_reference]
    .filter(Boolean)
    .join(" · ");
  return (
    <tr className="border-t border-border align-top">
      {ticks && (
        <td className="py-1.5 pr-2">
          {drop.status === "awaiting_despatch" && (
            <Checkbox
              aria-label={`Tick ${drop.destination} to despatch`}
              checked={ticks.ticked.has(drop.number)}
              disabled={busy}
              onCheckedChange={(checked) =>
                ticks.toggle(drop.number, checked === true)
              }
            />
          )}
        </td>
      )}
      <td className="py-1.5 pr-3">{drop.destination}</td>
      <td className="py-1.5 pr-3 tabular-nums">{drop.postcode ?? ""}</td>
      <td className="py-1.5 pr-3 text-right tabular-nums">
        {drop.kits.toLocaleString("en-GB")}
      </td>
      <td className="py-1.5 pr-3">{drop.status_label}</td>
      <td className="py-1.5 pr-3">{carrier}</td>
      {onRecordDespatched && (
        <td className="py-1.5 text-right">
          {drop.status === "awaiting_despatch" && (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              aria-label={`Record despatched: ${drop.destination}`}
              onClick={() => onRecordDespatched(drop.id)}
            >
              Record despatched…
            </Button>
          )}
        </td>
      )}
    </tr>
  );
}
